import { useCallback, useEffect, useState } from "react";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import { useNotification } from "@context/NotificationContext";
import { type Document } from "@features/vessel-call/create/store/useVesselCallForm.store";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { t } from "i18next";

const LANG_BASE = "vessel-call.create";

export const useDocumentUpload = (
  field: string,
  token: SasTokenPrefix,
  formData?: any,
  formStore?: any,
) => {
  const store = useUploadStore.getState();
  const [uploadEntries, setUploadEntries] = useState<UploadEntry[]>([]);

  const { notify } = useNotification();
  const isUploadedDocument = (entry: UploadEntry): entry is Document =>
    !(entry instanceof File) && typeof entry.url === "string" && entry.url.length > 0;

  useEffect(() => {
    const persisted = store.getFilesForKey(field) ?? [];
    setUploadEntries(persisted);
  }, [field]);

  //set form store with the uploaded document url
  const setFormDocument = useCallback(
    (doc: Document[]) => {
      formStore?.setState((state: any) => ({
        ...state,
        [field]: doc ? doc : [],
      }));
    },
    [field],
  );

  const setDocumentValue = useCallback(
    (docs: Document[]) => {
      if (formData?.setValue) {
        const isSubmitted = formData?.formState?.isSubmitted;
        formData.setValue(field, docs, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: Boolean(isSubmitted),
        });
      }
    },
    [formData, field],
  );

  const clearDocumentValue = useCallback(() => {
    setDocumentValue([]);
    if (formData?.trigger && formData?.formState?.isSubmitted) {
      void formData.trigger(field);
    }
  }, [setDocumentValue, formData, field]);

  useEffect(() => {
    if (!formData) return;

    const uploadedDocuments = uploadEntries.filter(isUploadedDocument);

    if (uploadedDocuments.length === 0) return;
    //check if the form url is different from the store url of the file
    const currentFormValue = formData.getValues?.(field);
    const isDifferent = JSON.stringify(currentFormValue) !== JSON.stringify(uploadEntries);

    if (uploadEntries.length && isDifferent) {
      setDocumentValue(uploadEntries as Document[]);
    }
  }, [uploadEntries, setDocumentValue, formData, field]);

  const uploadDocuments = useCallback(
    ({ files }: { files: File[] }): Promise<Document[]> =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "CreateVesselCall",
          action: "upload_vessel_call_document",
          entityType: "vessel_call_document",
        },
        async (transactionId) => {
          if (!files.length) return [];

          const file = files[0];
          const payloadForSasToken = {
            prefix: token,
            contentType: file.type || null,
            privilege: SasTokenPrivilege.WRITE,
            blobUrl: null,
          };

          const { blobUrl, sasToken } = await getSasToken(
            payloadForSasToken,
            ...(transactionId ? [transactionId] : []),
          );

          const [uploadResult] = await uploadFileToAzureBlob({
            file,
            blobUrl,
            sasToken,
            certificateType: field,
          });

          const documentUrl = uploadResult?.documentUrl ?? blobUrl;

          return [
            {
              name: file.name,
              url: documentUrl,
              type: file.type,
            },
          ];
        },
      ),
    [field],
  );

  const handleUploadError = useCallback(() => {
    notify({
      title: t(`${LANG_BASE}.notifications.vessel-call-upload-document-error-title`),
      description: t(`${LANG_BASE}.notifications.vessel-call-upload-document-error-description`),
      type: "error",
    });

    store.clearFilesForKey(field);
    setUploadEntries([]);
    setFormDocument([]);
    if (formData) clearDocumentValue();
  }, [field, setFormDocument, store, formData, clearDocumentValue]);

  const uploadMutation = useMutation({
    mutationFn: uploadDocuments,
    onSuccess: (result, variables) => {
      if (!result || result.length === 0) return;

      const file = variables.files[0];
      const uploaded = result[0];

      const entry = {
        name: uploaded.name ?? file?.name ?? "",
        url: uploaded.url,
        type: uploaded.type ?? file?.type ?? "",
      };

      if (!entry.url) return;

      setUploadEntries([entry]);
      store.setFilesForKey(field, [entry]);
      setFormDocument([entry]);

      if (formData) setDocumentValue([entry]);
    },

    onError: () => {
      handleUploadError();
    },
  });

  const handleUpload = useCallback(
    (files: File[]) => {
      if (!files.length) return;

      setDocumentValue([]);
      setUploadEntries([files[0]]);
      uploadMutation.mutate({ files });
    },
    [uploadMutation, setDocumentValue],
  );

  const handleRemove = useCallback(() => {
    store.clearFilesForKey(field);
    setFormDocument([]);
    setUploadEntries([]);
    if (formData) clearDocumentValue();
  }, [field, setFormDocument, store, formData, clearDocumentValue]);

  return {
    uploadEntries,
    setUploadEntries,
    handleUpload,
    handleRemove,
    setDocumentValue,
    clearDocumentValue,
    notSuccess: uploadMutation.isPending || uploadMutation.isIdle,
    isPending: uploadMutation.isPending,
  };
};
